import { compose, rotateDeg, scale, translate, type Matrix } from './matrix.ts';
import { normalisedPath, segmentsBounds } from './path.ts';
import type { SymbolDef, SymbolPort } from './types.ts';

export interface Placement {
  x: number;
  y: number;
  rotation: 0 | 90 | 180 | 270;
  mirrored: boolean;
}

/** Local symbol coordinates → sheet coordinates. Rotation and mirroring are about the symbol centre. */
export function placementMatrix(def: Pick<SymbolDef, 'width' | 'height'>, p: Placement): Matrix {
  const cx = def.width / 2;
  const cy = def.height / 2;
  return compose(
    translate(p.x + cx, p.y + cy),
    rotateDeg(p.rotation),
    scale(p.mirrored ? -1 : 1, 1),
    translate(-cx, -cy),
  );
}

/** Transform a symbol-local point into sheet coordinates. */
export function toSheet(def: SymbolDef, p: Placement, lx: number, ly: number): { x: number; y: number } {
  const m = placementMatrix(def, p);
  return { x: m[0] * lx + m[2] * ly + m[4], y: m[1] * lx + m[3] * ly + m[5] };
}

export function portPosition(def: SymbolDef, p: Placement, port: SymbolPort): { x: number; y: number } {
  return toSheet(def, p, port.x, port.y);
}

const ROT: Record<SymbolPort['dir'], number> = { up: 0, right: 90, down: 180, left: 270 };
const DIRS: SymbolPort['dir'][] = ['up', 'right', 'down', 'left'];

/** Direction a port faces once the symbol is placed. */
export function portDirection(port: SymbolPort, p: Placement): SymbolPort['dir'] {
  let a = ROT[port.dir];
  if (p.mirrored && (port.dir === 'left' || port.dir === 'right')) a = (a + 180) % 360;
  return DIRS[((a + p.rotation) % 360) / 90]!;
}

type Box = { minX: number; minY: number; maxX: number; maxY: number };
const localCache = new WeakMap<object, Box>();

/** Local bounds of a symbol: its nominal box plus any geometry drawn outside it (actuators...). */
export function localBounds(
  def: Pick<SymbolDef, 'width' | 'height'> & Partial<Pick<SymbolDef, 'paths' | 'fills' | 'dashed'>>,
): Box {
  const hit = localCache.get(def);
  if (hit) return hit;
  const b = { minX: 0, minY: 0, maxX: def.width, maxY: def.height };
  for (const d of [...(def.paths ?? []), ...(def.fills ?? []), ...(def.dashed ?? [])]) {
    const pb = segmentsBounds(normalisedPath(d));
    b.minX = Math.min(b.minX, pb.minX);
    b.minY = Math.min(b.minY, pb.minY);
    b.maxX = Math.max(b.maxX, pb.maxX);
    b.maxY = Math.max(b.maxY, pb.maxY);
  }
  localCache.set(def, b);
  return b;
}

/** Axis-aligned bounds of a placed symbol, in sheet coordinates. */
export function placedBounds(
  def: Pick<SymbolDef, 'width' | 'height'> & Partial<Pick<SymbolDef, 'paths' | 'fills' | 'dashed'>>,
  p: Placement,
) {
  const m = placementMatrix(def, p);
  const lb = localBounds(def);
  const pts = [
    [lb.minX, lb.minY],
    [lb.maxX, lb.minY],
    [lb.minX, lb.maxY],
    [lb.maxX, lb.maxY],
  ].map(([x, y]) => ({ x: m[0] * x! + m[2] * y! + m[4], y: m[1] * x! + m[3] * y! + m[5] }));
  const xs = pts.map((c) => c.x);
  const ys = pts.map((c) => c.y);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

/** Text width estimate (mm) for layout without a canvas: average glyph ≈ 0.56 em. */
export function estimateTextWidth(text: string, size: number): number {
  return text.length * size * 0.56;
}

/** Size of a free-text annotation, from its text. */
export function freeTextSize(props: Record<string, unknown>): {
  width: number;
  height: number;
  size: number;
} {
  const size = typeof props.size === 'number' && props.size > 0 ? props.size : 3.5;
  const lines = String(props.text ?? '').split('\n');
  const w = Math.max(...lines.map((l) => estimateTextWidth(l, size)), size);
  return { width: w, height: lines.length * size * 1.25, size };
}

/**
 * Nominal size of a placed element: the symbol box, or the size given by its properties for
 * free text and rooms. Rotation and mirroring happen about the centre of this box everywhere
 * (canvas, exports, hit-testing).
 */
export function elementDims(
  def: SymbolDef,
  el: { props: Record<string, unknown> },
): { width: number; height: number } {
  if (def.freeText) {
    const { width, height } = freeTextSize(el.props);
    return { width, height };
  }
  if (def.room || def.box) return roomSize(el.props);
  return def;
}

/** Bounds of a placed element, accounting for free-text annotations and rooms. */
export function elementBounds(def: SymbolDef, el: Placement & { props: Record<string, unknown> }) {
  if (def.freeText || def.room || def.box) return placedBounds(elementDims(def, el), el);
  return placedBounds(def, el);
}

/** Size of a room rectangle (mm). */
export function roomSize(props: Record<string, unknown>): { width: number; height: number } {
  const n = (v: unknown, d: number) => (typeof v === 'number' && v > 0 ? v : d);
  return { width: n(props.w, 40), height: n(props.h, 30) };
}
